import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Formik, Form, FieldArray } from 'formik';
import { object, array, string } from 'yup';

import { Dispatch } from '../../store/store';
import { MetaField } from '../../store/models/cycles';
import { FieldInput } from '../FieldInput/FieldInput';
import NewFieldForm from './components/NewFieldForm';
import { RANGE_DEFAULT_META_FIELD } from './constants/common';

interface DefaultMetaFieldsProps {
  defaultMetaTags: Record<string, string>;
  addDefaultMetaTag: (data: { id: string; metaField: MetaField }) => Promise<void>;
  removeDefaultMetaTag: (data: { id: string; key: string }) => Promise<void>;
  updateDefaultMetaTags: (data: { id: string; updatedFields: Record<string, string> }) => Promise<MetaField[]>;
}

const DefaultMetaFields: React.FC<DefaultMetaFieldsProps> = ({
  defaultMetaTags,
  addDefaultMetaTag,
  removeDefaultMetaTag,
  updateDefaultMetaTags,
}) => {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);

  const initialFields: MetaField[] = useMemo(
    () => Object.entries(defaultMetaTags).map(([key, value]) => ({ key, value })),
    [defaultMetaTags]
  );

  const validationSchema = object().shape({
    metaFields: array()
      .of(
        object().shape({
          key: string()
            .required('Key is required')
            .min(RANGE_DEFAULT_META_FIELD.MIN, `Key must be at least ${RANGE_DEFAULT_META_FIELD.MIN} characters`)
            .max(RANGE_DEFAULT_META_FIELD.MAX, `Key must be max ${RANGE_DEFAULT_META_FIELD.MAX} characters`),
          value: string()
            .required('Value is required')
            .min(RANGE_DEFAULT_META_FIELD.MIN, `Value must be at least ${RANGE_DEFAULT_META_FIELD.MIN} characters`)
            .max(RANGE_DEFAULT_META_FIELD.MAX, `Value must be max ${RANGE_DEFAULT_META_FIELD.MAX} characters`),
        })
      )
      .min(1, 'At least one field is required'),
  });

  const handleSubmit = async (values: { metaFields: MetaField[] }) => {
    try {
      setIsLoading(true);
      const formattedFields = values.metaFields.reduce<Record<string, string>>((acc, field) => {
        if (field.key.trim()) acc[field.key] = field.value;
        return acc;
      }, {});
      await updateDefaultMetaTags({ id, updatedFields: formattedFields });
    } catch (error) {
      console.error('Error updating fields:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-20 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark p-7">
      <div className="border-b border-stroke py-4 dark:border-strokedark">
        <h3 className="font-medium text-black dark:text-white">Default Meta Fields</h3>
      </div>

      <Formik
        initialValues={{ metaFields: initialFields }}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({
          values,
          setFieldValue,
          errors,
          touched,
          setFieldTouched,
        }) => (
          <>
            <Form className='pt-7'>
              <FieldArray name="metaFields">
                {({ remove }) => (
                  <>
                    {values.metaFields.length === 0 ? (
                      <p className="text-gray-500 text-sm">
                        No default meta fields available. Add new fields below.
                      </p>
                    ) : (
                      values.metaFields.map((field, index) => (
                        <FieldInput
                          key={index}
                          keyError={errors?.metaFields?.[index]?.['key']}
                          valueError={errors?.metaFields?.[index]?.['value']}
                          field={field}
                          originalField={field}
                          onRemove={async () => {
                            remove(index);
                            if (field.key) {
                              try {
                                await removeDefaultMetaTag({ id, key: field.key });
                              } catch (err) {
                                console.error('Remove failed', err);
                              }
                            }
                          }}
                          onUpdate={(updatedField) => {
                            setFieldValue(`metaFields[${index}]`, updatedField);
                            setFieldTouched(`metaFields[${index}]`, true);
                          }}
                        />
                      ))
                    )}
                    {values.metaFields.length > 0 && (
                      <button
                        type="submit"
                        className="mt-4 w-fit rounded-md bg-primary px-4 py-2 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
                        disabled={isLoading || !!errors.metaFields || !touched.metaFields}
                      >
                        {isLoading ? 'Updating…' : 'Update'}
                      </button>
                    )}
                  </>
                )}
              </FieldArray>
            </Form>

            <NewFieldForm
              isLoading={isLoading}
              range={{ min: RANGE_DEFAULT_META_FIELD.MIN, max: RANGE_DEFAULT_META_FIELD.MAX }}
              onAdd={async (newField) => {
                try {
                  setIsLoading(true);
                  await addDefaultMetaTag({ id, metaField: newField });

                  const updatedMetaFields = [...values.metaFields, newField];
                  setFieldValue('metaFields', updatedMetaFields);
                } catch (err) {
                  console.error('Add failed', err);
                } finally {
                  setIsLoading(false);
                }
              }}
            />
          </>
        )}
      </Formik>
    </div>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  addDefaultMetaTag: dispatch.contracts.addDefaultMetaTag,
  removeDefaultMetaTag: dispatch.contracts.removeDefaultMetaTag,
  updateDefaultMetaTags: dispatch.contracts.updateDefaultMetaTags,
});

export default connect(null, mapDispatch)(DefaultMetaFields);